import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Medal, Clock } from "lucide-react";
import { Badge } from "./ui/badge";
import { fmtSecs, isTimeTieBreak } from "../lib/liveTime";

const CONFETTI_COLORS = ["#0EA5E9", "#F97316", "#22C55E", "#FBBF24", "#A855F7"];

function Confetti() {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    setParticles(Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 1.5,
      duration: 2.5 + Math.random() * 2,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: 6 + Math.random() * 8,
      round: Math.random() > 0.5,
    })));
  }, []);

  return (
    <div className="confetti-container">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          initial={{ x: `${p.x}vw`, y: -20, rotate: 0, opacity: 1 }}
          animate={{ y: "110vh", rotate: 720, opacity: 0 }}
          transition={{ duration: p.duration, delay: p.delay, ease: "linear" }}
          style={{
            position: "absolute",
            backgroundColor: p.color,
            width: p.size,
            height: p.size,
            borderRadius: p.round ? "50%" : "2px",
          }}
        />
      ))}
    </div>
  );
}

const PODIUM_CONFIG = {
  1: {
    height: "h-36 sm:h-44",
    bar: "bg-gradient-to-t from-amber-500 to-amber-300 dark:from-amber-600 dark:to-amber-400",
    ring: "ring-amber-400",
    delay: 1.2,
    icon: <Crown className="w-6 h-6 text-amber-500 fill-amber-400" />,
  },
  2: {
    height: "h-24 sm:h-32",
    bar: "bg-gradient-to-t from-slate-400 to-slate-300 dark:from-slate-500 dark:to-slate-400",
    ring: "ring-slate-400",
    delay: 0.7,
    icon: <Medal className="w-5 h-5 text-slate-500" />,
  },
  3: {
    height: "h-16 sm:h-24",
    bar: "bg-gradient-to-t from-orange-400 to-orange-300 dark:from-orange-500 dark:to-orange-400",
    ring: "ring-orange-400",
    delay: 0.3,
    icon: <Medal className="w-5 h-5 text-orange-500" />,
  },
};

export const LivePodium = ({ entries, meId, showConfetti = true }) => {
  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);
  // Podium display order: 2nd, 1st, 3rd
  const columns = [top3[1], top3[0], top3[2]].filter(Boolean);

  return (
    <div data-testid="live-podium">
      {showConfetti && <Confetti />}

      <div className="flex items-end justify-center gap-3 sm:gap-5 pt-16 pb-2 px-2">
        {columns.map((entry) => {
          const cfg = PODIUM_CONFIG[entry.rank];
          const isMe = meId && entry.participant_id === meId;
          return (
            <div key={entry.participant_id} className="flex flex-col items-center w-24 sm:w-32" data-testid={`podium-rank-${entry.rank}`}>
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.5 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: cfg.delay + 0.3, type: "spring", stiffness: 300, damping: 15 }}
                className="flex flex-col items-center mb-2"
              >
                {cfg.icon}
                <p className={`text-sm font-semibold text-center leading-tight mt-1 max-w-full truncate ${isMe ? "text-primary" : "text-foreground"}`}>
                  {entry.nickname}
                  {isMe && <span className="block text-[10px] text-primary font-normal">(you)</span>}
                </p>
                <Badge variant="outline" className="mt-1 text-xs">{entry.score} pts</Badge>
                {entry.total_time != null && (
                  <span className="mt-1 text-[11px] text-muted-foreground flex items-center gap-1" data-testid={`podium-time-${entry.rank}`}>
                    <Clock className="w-3 h-3" /> {fmtSecs(entry.total_time)}
                    {isTimeTieBreak(entries, entry) && <span className="text-amber-600 dark:text-amber-400" title="Tie broken on total answer time">*</span>}
                  </span>
                )}
              </motion.div>
              <motion.div
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ delay: cfg.delay, duration: 0.5, ease: "easeOut" }}
                style={{ transformOrigin: "bottom" }}
                className={`w-full ${cfg.height} ${cfg.bar} rounded-t-xl ring-1 ${cfg.ring} flex items-start justify-center pt-2 shadow-lg`}
              >
                <span className="font-heading font-extrabold text-2xl sm:text-3xl text-white drop-shadow">
                  {entry.rank}
                </span>
              </motion.div>
            </div>
          );
        })}
      </div>

      {rest.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2 }}
          className="mt-4 space-y-1.5"
          data-testid="podium-rest-list"
        >
          {rest.map((entry) => (
            <div
              key={entry.participant_id}
              className={`flex items-center gap-3 p-2.5 rounded-xl text-sm ${
                meId && entry.participant_id === meId ? "bg-primary/10 border border-primary/30" : "bg-muted/40"
              }`}
            >
              <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
                {entry.rank}
              </span>
              <span className="flex-1 min-w-0">
                <span className="font-medium truncate block">
                  {entry.nickname}
                  {meId && entry.participant_id === meId && <span className="text-xs text-primary ml-1">(you)</span>}
                </span>
                {entry.total_time != null && (
                  <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {fmtSecs(entry.total_time)}
                    {isTimeTieBreak(entries, entry) && <span className="text-amber-600 dark:text-amber-400">*</span>}
                  </span>
                )}
              </span>
              <Badge variant="outline" className="text-xs">{entry.score} pts</Badge>
            </div>
          ))}
        </motion.div>
      )}

      {entries.length > 1 && entries.some((e) => isTimeTieBreak(entries, e)) && (
        <p className="mt-3 text-center text-xs text-muted-foreground" data-testid="podium-tiebreak-note">
          * Equal points — placed by fastest total answer time
        </p>
      )}
    </div>
  );
};
